import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Offcanvas } from 'react-bootstrap';
import {
  IconX,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useAgentsFlow } from '../../../contexts/AgentsFlowContext';
import { MEDIA_ANALYSIS_PRESETS, FAQ_BEHAVIOR_TEMPLATES } from '../../../constants';
import { ICON_MAP } from '../../../constants/iconMap';
import { NODE_CATEGORIES } from '../../constants/nodeCategories';
import AgentsFlowService from '../../../services/AgentsFlowService';
import ActionFields from './ActionFields';
import ConditionFields from './ConditionFields';
import CapabilityFields from './CapabilityFields';
import ExtractVariablesEditor from './ExtractVariablesEditor';
import CapabilityGuideModal from '../CapabilityGuideModal';
import ApiCallGuideModal from '../ApiCallGuideModal';

/**
 * ConfigPanel - Complete configuration panel for all node types.
 *
 * Renders as an Offcanvas (side panel) and delegates to:
 *  - ActionFields     for action nodes
 *  - ConditionFields  for condition nodes
 *  - CapabilityFields for capability and integration nodes
 */
const ConfigPanel = ({ node, onClose, onUpdate, nodes }) => {
  const { state } = useAgentsFlow();
  const { id: agentId } = useParams();
  const integrations = state.editor?.integrations || [];

  const [localConfig, setLocalConfig] = useState({});
  const [localLabel, setLocalLabel] = useState('');
  const [mediaList, setMediaList] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  // Initialize local state when node changes (merge with defaults for backward compat)
  useEffect(() => {
    if (node) {
      const savedConfig = node.data?.config || {};
      const nodeKey = node.data?.key;
      // Buscar defaults do NODE_CATEGORIES para merge com configs antigos
      const categoryItem = NODE_CATEGORIES.flatMap(c => c.items).find(i => i.key === nodeKey);
      const defaultConfig = categoryItem?.config || {};
      setLocalConfig({ ...defaultConfig, ...savedConfig });
      setLocalLabel(node.data?.label || '');
    }
  }, [node]);

  // Load media list for send_media nodes
  useEffect(() => {
    const currentKey = node?.data?.key;
    if ((currentKey === 'send_media' || currentKey === 'media_library') && agentId) {
      setMediaLoading(true);
      AgentsFlowService.listMedia(agentId)
        .then(res => setMediaList(res.data || []))
        .catch(() => setMediaList([]))
        .finally(() => setMediaLoading(false));
    }
  }, [node?.data?.key, agentId]);

  if (!node) return null;

  // Get node key (capability or action type)
  const nodeKey = node.data?.key || node.data?.capability || node.data?.actionType;
  const nodeType = node.type;

  const handleSave = () => {
    // Strip transient keys (prefixed with _) before persisting
    const configToSave = Object.fromEntries(
      Object.entries(localConfig).filter(([k]) => !k.startsWith('_'))
    );
    onUpdate(node.id, {
      ...node.data,
      label: localLabel,
      config: configToSave
    });
    onClose();
  };

  const handleLabelChange = (e) => {
    setLocalLabel(e.target.value);
  };

  const handleConfigChange = (key, value) => {
    // Media analysis: when preset changes, auto-fill the prompt_template textarea
    if (nodeKey === 'media_analysis' && key === 'prompt_preset' && value !== 'custom') {
      const presetText = MEDIA_ANALYSIS_PRESETS[value] || '';
      setLocalConfig(prev => ({ ...prev, [key]: value, prompt_template: presetText }));
      return;
    }
    // FAQ: when template changes, auto-fill the behavior_prompt textarea
    if (nodeKey === 'faq' && key === 'behavior_prompt_template' && value) {
      const templateText = FAQ_BEHAVIOR_TEMPLATES[value] || '';
      setLocalConfig(prev => ({ ...prev, [key]: value, behavior_prompt: templateText }));
      return;
    }
    // FAQ: when user edits behavior_prompt manually, set template to 'custom'
    if (nodeKey === 'faq' && key === 'behavior_prompt') {
      const currentTemplate = localConfig.behavior_prompt_template;
      const templateText = currentTemplate ? (FAQ_BEHAVIOR_TEMPLATES[currentTemplate] || '') : '';
      if (currentTemplate && value !== templateText) {
        setLocalConfig(prev => ({ ...prev, [key]: value, behavior_prompt_template: '' }));
        return;
      }
    }
    setLocalConfig(prev => ({ ...prev, [key]: value }));
  };

  const renderDefaultPlaceholder = (type) => (
    <div className="afc-config-placeholder">
      <IconInfoCircle size={24} stroke={1.5} />
      <p>Configure este {type} usando os campos acima</p>
    </div>
  );

  // Render configuration fields based on node type and key
  const renderFields = () => {
    // ========== ACTIONS ==========
    if (nodeType === 'action') {
      return (
        <ActionFields
          nodeKey={nodeKey}
          nodeId={node.id}
          localConfig={localConfig}
          handleConfigChange={handleConfigChange}
          nodes={nodes}
          agentId={agentId}
          integrations={integrations}
          mediaList={mediaList}
          mediaLoading={mediaLoading}
          renderDefaultPlaceholder={renderDefaultPlaceholder}
        />
      );
    }

    // ========== CONDITIONS ==========
    if (nodeType === 'condition') {
      return (
        <ConditionFields
          nodeKey={nodeKey}
          localConfig={localConfig}
          handleConfigChange={handleConfigChange}
          renderDefaultPlaceholder={renderDefaultPlaceholder}
          agentId={agentId}
          nodeId={node.id}
          nodes={nodes}
        />
      );
    }

    // ========== CAPABILITIES & INTEGRATIONS ==========
    if (nodeType === 'capability' || nodeType === 'integration') {
      return (
        <>
          <CapabilityFields
            nodeKey={nodeKey}
            nodeType={nodeType}
            localConfig={localConfig}
            handleConfigChange={handleConfigChange}
            agentId={agentId}
            integrations={integrations}
            mediaList={mediaList}
            mediaLoading={mediaLoading}
            renderDefaultPlaceholder={renderDefaultPlaceholder}
          />
          {/* Extração de variáveis via structured output —
              só faz sentido em capabilities (que rodam LLM).
              Integrações são chamadas externas, sem LLM. */}
          {nodeType === 'capability' && (
            <>
              <hr className="afc-divider" />
              <ExtractVariablesEditor
                variables={localConfig.extract_variables || []}
                onChange={(v) => handleConfigChange('extract_variables', v)}
              />
            </>
          )}
        </>
      );
    }

    return renderDefaultPlaceholder('nó');
  };

  // Get display icon - APENAS pelo ICON_MAP (não usar data.icon que não pode ser serializado)
  const IconComponent = ICON_MAP[node.data?.iconKey] || ICON_MAP[nodeKey];

  return (
    <Offcanvas show={!!node} onHide={onClose} placement="end" className="afc-config-panel">
      <Offcanvas.Header>
        <div className="afc-config-header">
          <div className="afc-config-icon" style={{ background: node.data?.gradient || '#6366f1' }}>
            {IconComponent && <IconComponent size={20} stroke={1.5} />}
          </div>
          <div>
            <Offcanvas.Title className="afc-config-title">
              {localLabel || node.data?.label}
              {nodeType === 'capability' && <CapabilityGuideModal capabilityKey={nodeKey} />}
              {nodeKey === 'api_call' && <ApiCallGuideModal />}
            </Offcanvas.Title>
            <span className="afc-config-type">{nodeType} • {nodeKey}</span>
          </div>
        </div>
        <button className="afc-config-close" onClick={onClose}>
          <IconX size={20} />
        </button>
      </Offcanvas.Header>
      <Offcanvas.Body className="afc-config-body">
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Nome do Nó</Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localLabel}
              onChange={handleLabelChange}
              placeholder="Digite um nome para identificar este nó"
            />
          </Form.Group>


          {renderFields()}
        </Form>
      </Offcanvas.Body>
      <div className="afc-config-footer">
        <Button variant="outline-secondary" onClick={onClose} className="afc-btn-secondary">
          Cancelar
        </Button>
        <Button onClick={handleSave} className="afc-btn-primary">
          <IconCheck size={18} />
          Salvar
        </Button>
      </div>
    </Offcanvas>
  );
};

export default ConfigPanel;
